import React, { useState } from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import { operatives } from '../../modelsConceptuals';
import { fileraDe, respostesOrfes, netejaOrfes, diferencies, contestades, RESPOSTES } from '../../comparacioSSM';

// L'ETAPA 5: LA COMPARACIÓ.
//
// Les regles són a `src/comparacioSSM.js`, amb proves pròpies (`npm run prova:comparacio`).
//
// El model conceptual **no descriu el món**: descriu què hauria de passar si el sistema fos com
// diu una definició arrel. Aquí es posa **al costat del que passa de debò**, activitat per
// activitat, i el que en surt no és una nota sinó **la llista de coses de què val la pena
// parlar** a l'etapa 6.
//
// De les quatre maneres de comparar que dona Checkland, aquí hi ha **l'interrogatori formal**:
// és la que es fa servir a l'aula perquè deixa rastre i es pot discutir en grup. Les altres tres
// no hi són **i no hi són enunciades**: una pantalla que anomena el que no fa és el defecte que
// aquesta aplicació ja arrossegava.

const CLAU_RESPOSTA = { yes: 'cpEsSi', partly: 'cpEsAMitges', no: 'cpEsNo' };

const ComparisonStage = ({ data, onUpdate }) => {
  const { t } = useTranslation();
  const models = data.conceptualModels || [];
  const rootDefinitions = data.rootDefinitions || [];
  const comparison = data.comparison || {};
  const matrius = comparison.matrices || [];

  const [triat, setTriat] = useState(models[0]?.id || null);
  const model = models.find((m) => m.id === triat) || null;
  const matriu = matrius.find((x) => x.conceptualModelId === triat) || null;
  const respostes = matriu?.rows || {};

  const rd = rootDefinitions.find((x) => x.id === model?.rootDefinitionId);
  const files = model ? fileraDe(model, respostes) : [];
  const orfes = model ? respostesOrfes(model, respostes) : [];
  const difs = diferencies(files);

  const desa = (noves) => {
    const nova = { id: matriu?.id || `mx_${Date.now()}`, conceptualModelId: triat, rows: noves };
    onUpdate({ comparison: { ...comparison,
      matrices: matrius.some((x) => x.conceptualModelId === triat)
        ? matrius.map((x) => (x.conceptualModelId === triat ? nova : x))
        : [...matrius, nova] } });
  };

  const contesta = (activityId, camp, valor) => desa({
    ...respostes,
    [activityId]: { exists: '', how: '', judged: '', comments: '', ...respostes[activityId], [camp]: valor },
  });

  if (!models.length) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-md p-6">
          <p className="text-gray-700">{t('ssm.cpCalModel')}</p>
        </div>
      </div>
    );
  }

  const CAMPS = [['how', 'cpCom'], ['judged', 'cpJutja'], ['comments', 'cpComentaris']];

  return (
    <MarcEtapa className="p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-gray-700">{t('ssm.cpQue')}</p>
            {files.length > 0 && (
              <span className="flex-shrink-0 text-xs text-gray-500 tabular-nums">
                {t('ssm.cpContestades', { fetes: contestades(files), total: files.length })}
              </span>
            )}
          </div>
          {models.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {models.map((m) => {
                const seva = rootDefinitions.find((x) => x.id === m.rootDefinitionId);
                return (
                  <button key={m.id} type="button" onClick={() => setTriat(m.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      m.id === triat ? 'bg-primary-700 text-white border-primary-700'
                                     : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {seva?.systemName || t('ssm.untitled')}
                  </button>
                );
              })}
            </div>
          )}
          {rd && (
            <p className="mt-3 text-xs text-gray-500">
              {t('ssm.cmContraLaRD')}: <span className="text-gray-700">
                {rd.rootDefinition || t('ssm.cmRDBuida')}</span>
            </p>
          )}
        </div>

        {/* Les respostes que han quedat penjades d'una activitat que ja no hi és. Es diuen i qui
            compara decideix: amagar-les seria amagar feina, i esborrar-les soles, perdre-la. */}
        {orfes.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white border border-warn-400 text-sm">
            <span className="text-gray-700">{t('ssm.cpOrfes', { n: orfes.length })}</span>
            <button type="button" onClick={() => desa(netejaOrfes(model, respostes))}
              className="ml-auto text-xs font-medium text-primary-700 hover:underline">
              {t('ssm.cpNetejaOrfes')}
            </button>
          </div>
        )}

        {operatives(model).length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-md p-6">
            <p className="text-gray-700">{t('ssm.cpSenseActivitats')}</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-3 py-2 font-semibold text-gray-700 w-56">{t('ssm.cpActivitat')}</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 w-40">{t('ssm.cpPassa')}</th>
                  {CAMPS.map(([, etq]) => (
                    <th key={etq} className="px-3 py-2 font-semibold text-gray-700">{t(`ssm.${etq}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.activityId} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <span className="text-xs text-gray-500 mr-1">{f.n}.</span>
                      <span className="text-gray-900">{f.name || t('ssm.cmActivitatN', { n: f.n })}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {RESPOSTES.map((v) => (
                          <button key={v} type="button"
                            onClick={() => contesta(f.activityId, 'exists', f.exists === v ? '' : v)}
                            className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                              f.exists === v ? 'bg-primary-700 text-white border-primary-700'
                                             : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            {t(`ssm.${CLAU_RESPOSTA[v]}`)}
                          </button>
                        ))}
                      </div>
                    </td>
                    {CAMPS.map(([camp, etq]) => (
                      <td key={camp} className="px-3 py-2">
                        <textarea rows={2} value={f[camp] || ''}
                          onChange={(e) => contesta(f.activityId, camp, e.target.value)}
                          placeholder={t(`ssm.${etq}Ph`)}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm
                                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* EL QUE EN SURT. Sense això la taula és un exercici d'omplir caselles: aquí és on es
            veu que cada diferència és una cosa de què parlar a l'etapa 6. */}
        {difs.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="text-sm font-semibold text-gray-900">{t('ssm.cpDeQueParlar')}</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">{t('ssm.cpDeQueParlarWhy')}</p>
            <ul className="space-y-1.5 text-sm">
              {difs.map((d) => (
                <li key={`${d.activityId}-${d.clau}`} className="flex gap-2">
                  <span className="text-warn-600">•</span>
                  <span className="text-gray-700">
                    <span className="font-medium text-gray-900">{d.name || t('ssm.untitled')}</span>
                    {' — '}{t(d.clau)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </MarcEtapa>
  );
};

export default ComparisonStage;
